/**
 * Literacy route components — thin wrappers for React Router.
 *
 * Two modes:
 *   - Patient mode: /patients/:patientId/literacy/* — tracks progress per patient
 *   - Explore mode: /literacy/* — browse curriculum without patient context
 */

import React, { useState, useRef, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, Typography, CircularProgress, Chip } from "@mui/material"
import MenuBookIcon from "@mui/icons-material/MenuBook"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import CloseIcon from "@mui/icons-material/Close"
import {
  MessageCircle, Volume1, Volume2, Puzzle, PenLine, VolumeX,
  Sparkles, Handshake, Target, Waves, Blocks, RefreshCw,
  Link, BookOpen, CheckCircle, Eye, Ear, PencilLine, Layers,
  Mic, Speaker, ArrowLeft, Play, Lock, X, Check, RotateCcw, Award,
} from "lucide-react"
import { LiteracyProvider, useLiteracy } from "../../contexts/LiteracyContext"
import { curriculum, getPhaseById, getLessonById, getPhaseForLesson } from "./curriculum"
import { patients as patientsApi } from "../../api/endpoints"
import type { Phase, Lesson, LiteracyProgress, DrillConfig, DrillAttempt, DrillProgress } from "../../types/literacy"
import { EASI_DESIGN } from "../../theme/design-tokens"

const d = EASI_DESIGN

// ── Phase accent colors (emerald/teal palette) ──────────────────────────────

const PHASE_ACCENTS = [
  { bg: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)", border: "#6EE7B7", badge: "#059669", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)", border: "#67E8F9", badge: "#0891B2", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)", border: "#93C5FD", badge: "#2563EB", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)", border: "#C4B5FD", badge: "#7C3AED", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #D1FAE5 0%, #CFFAFE 100%)", border: "#5EEAD4", badge: "#0D9488", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", border: "#86EFAC", badge: "#16A34A", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)", border: "#5EEAD4", badge: "#0F766E", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)", border: "#93C5FD", badge: "#1D4ED8", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)", border: "#A78BFA", badge: "#6D28D9", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)", border: "#34D399", badge: "#047857", badgeText: "#fff" },
  { bg: "linear-gradient(135deg, #CFFAFE 0%, #D1FAE5 100%)", border: "#6EE7B7", badge: "#059669", badgeText: "#fff" },
]

// ── EASI navy for icons ──────────────────────────────────────────────────────

const ICON_COLOR = "#1E3A5F"
const ICON_SIZE = 20

// ── Lesson icon picker (Lucide React icons) ──────────────────────────────────

function getLessonIcon(title: string): React.ReactElement {
  const t = title.toLowerCase()
  const props = { size: ICON_SIZE, color: ICON_COLOR, strokeWidth: 2 }
  if (t.includes("syllable") || t.includes("cv ") || t.includes("vc ") || t.includes("combining")) return <MessageCircle {...props} />
  if (t.includes("short") && t.includes("vowel")) return <Volume1 {...props} />
  if (t.includes("long") && (t.includes("vowel") || t.includes("a") || t.includes("e") || t.includes("i") || t.includes("o") || t.includes("u"))) return <Volume2 {...props} />
  if (t.includes("consonant") && !t.includes("-le")) return <Puzzle {...props} />
  if (t.includes("cvc")) return <PenLine {...props} />
  if (t.includes("silent")) return <VolumeX {...props} />
  if (t.includes("soft c") || t.includes("soft g")) return <Sparkles {...props} />
  if (t.includes("vowel team") || t.includes("ai") || t.includes("ea") || t.includes("oa")) return <Handshake {...props} />
  if (t.includes("r-controlled") || t.includes("bossy r")) return <Target {...props} />
  if (t.includes("diphthong") || t.includes("oi") || t.includes("ou") || t.includes("oo")) return <Waves {...props} />
  if (t.includes("-le") || t.includes("-ble") || t.includes("-tle") || t.includes("-gle") || t.includes("-ple") || t.includes("-cle") || t.includes("-zle") || t.includes("-fle") || t.includes("-dle") || t.includes("-kle")) return <Blocks {...props} />
  if (t.includes("review") || t.includes("mixed")) return <RefreshCw {...props} />
  if (t.includes("digraph")) return <Link {...props} />
  return <BookOpen {...props} />
}

// ── CSS keyframes (injected once) ───────────────────────────────────────────

const KEYFRAMES_ID = "literacy-keyframes"
function ensureKeyframes() {
  if (typeof document === "undefined") return
  if (document.getElementById(KEYFRAMES_ID)) return
  const style = document.createElement("style")
  style.id = KEYFRAMES_ID
  style.textContent = `
    @keyframes litFadeInUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes litDotPulse {
      0%   { transform: scale(1); opacity: 0.7; }
      50%  { transform: scale(1.6); opacity: 1; }
      100% { transform: scale(1); opacity: 0.7; }
    }
  `
  document.head.appendChild(style)
}

// ── Viewport-triggered reveal hook ──────────────────────────────────────────

function useInView(rootMargin = "0px 0px -40px 0px") {
  const ref = useRef<HTMLDivElement | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect() } },
      { rootMargin, threshold: 0.1 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [rootMargin])

  return { ref, visible }
}

// ── Patient info hook ────────────────────────────────────────────────────────

function usePatientInfo(patientId: string | undefined) {
  const [name, setName] = useState("")
  const [initials, setInitials] = useState("")

  useEffect(() => {
    if (!patientId) return
    patientsApi.get(patientId).then((res) => {
      const p = res.data as any
      if (p?.firstName && p?.lastName) {
        setName(`${p.firstName} ${p.lastName}`)
        setInitials(`${p.firstName[0]}${p.lastName[0]}`.toUpperCase())
      }
    }).catch(() => {
      // Patient fetch failed — header will show without name
    })
  }, [patientId])

  return { name, initials }
}

// ── Lesson progress helper ──────────────────────────────────────────────────

function getLessonStatus(lessonId: string, progress: LiteracyProgress | null): "completed" | "in-progress" | "not-started" {
  if (!progress?.phases) return "not-started"
  for (const phaseProgress of Object.values(progress.phases)) {
    const lp = (phaseProgress as any)?.lessons?.[lessonId]
    if (lp) {
      if (lp.completed || lp.masteryAchieved) return "completed"
      // Has any drill attempts?
      const drills = lp.drills || {}
      const hasAttempts = Object.values(drills).some((d: any) => d.attempts?.length > 0)
      if (hasAttempts) return "in-progress"
    }
  }
  return "not-started"
}

function getLessonAccuracy(lessonId: string, progress: LiteracyProgress | null): number | null {
  if (!progress?.phases) return null
  for (const phaseProgress of Object.values(progress.phases)) {
    const lp = (phaseProgress as any)?.lessons?.[lessonId]
    if (lp?.drills) {
      const drills = Object.values(lp.drills) as any[]
      const withAttempts = drills.filter((d) => d.attempts?.length > 0)
      if (withAttempts.length === 0) return null
      const totalCorrect = withAttempts.reduce((sum: number, d: any) =>
        sum + d.attempts.filter((a: any) => a.correct).length, 0)
      const totalAttempts = withAttempts.reduce((sum: number, d: any) =>
        sum + d.attempts.length, 0)
      return totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : null
    }
  }
  return null
}

// ── Drill count dots (with pulse-on-appear) ─────────────────────────────────

function DrillDots({ count, accent, animate }: { count: number; accent: string; animate: boolean }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
      {Array.from({ length: Math.min(count, 6) }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 6, height: 6, borderRadius: "50%",
            bgcolor: accent, opacity: 0.7,
            ...(animate ? { animation: `litDotPulse 500ms ease ${i * 80}ms both` } : {}),
          }}
        />
      ))}
      <Typography variant="caption" sx={{ color: d.color.slate400, fontSize: 11, ml: 0.25 }}>
        {count} {count === 1 ? "drill" : "drills"}
      </Typography>
    </Box>
  )
}

// ── Shared loading fallback ──────────────────────────────────────────────────

function LiteracyLoading() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
      <CircularProgress sx={{ color: d.color.emerald }} />
    </Box>
  )
}

// ── Patient header bar ──────────────────────────────────────────────────────

function PatientHeader({ name, initials, patientId }: { name: string; initials: string; patientId: string }) {
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 2,
        px: 3, py: 1.5,
        background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 50%, #F0FDFA 100%)",
        borderBottom: `1px solid ${d.color.emeraldBorder}`,
        position: "sticky", top: 0, zIndex: d.zIndex.sticky + 1,
        backdropFilter: "blur(12px)",
      }}
    >
      <Box
        component="a"
        href="/"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault()
          window.location.href = "/"
        }}
        sx={{
          display: "flex", alignItems: "center", gap: 0.5,
          color: d.color.emerald, textDecoration: "none",
          fontSize: 13, fontWeight: 600,
          cursor: "pointer",
          transition: "color 0.2s ease, transform 0.2s ease",
          "&:hover": { color: d.color.emeraldDark, transform: "translateX(-2px)" },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        {name ? `Back to ${name.split(" ")[0]}` : "Back"}
      </Box>
      <Box sx={{ flex: 1 }} />
      <Box
        sx={{
          width: 34, height: 34, borderRadius: "50%",
          bgcolor: d.color.emerald, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 13, flexShrink: 0,
          boxShadow: "0 2px 8px rgba(16,185,129,0.25)",
        }}
      >
        {initials || "?"}
      </Box>
      <Typography sx={{ fontWeight: 700, color: d.color.slate800, fontSize: 15 }}>
        {name || "Patient"}
        <Typography component="span" sx={{ color: d.color.slate400, fontWeight: 500, mx: 1 }}>&mdash;</Typography>
        <Typography component="span" sx={{ color: d.color.emerald, fontWeight: 700 }}>Lightspeed Literacy</Typography>
      </Typography>
    </Box>
  )
}

// ── Phase header ────────────────────────────────────────────────────────────

function PhaseHeader({ phase, accent, totalDrills }: { phase: Phase; accent: typeof PHASE_ACCENTS[0]; totalDrills: number }) {
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 2,
        p: 2, mb: 2, borderRadius: d.radius.lg,
        background: accent.bg,
        borderLeft: `4px solid ${accent.border}`,
        position: "sticky", top: 0, zIndex: d.zIndex.sticky,
        backdropFilter: "blur(12px)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          boxShadow: `0 4px 20px ${accent.badge}20, 0 2px 12px rgba(0,0,0,0.06)`,
        },
        "&:hover .phase-badge": {
          transform: "scale(1.08)",
        },
      }}
    >
      <Box
        className="phase-badge"
        sx={{
          width: 44, height: 44, borderRadius: "50%",
          bgcolor: accent.badge, color: accent.badgeText,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 18, flexShrink: 0,
          boxShadow: `0 3px 10px ${accent.badge}40`,
          transition: "transform 0.2s ease",
        }}
      >
        {phase.number}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: d.color.slate800, fontSize: 16, lineHeight: 1.3 }}>
          {phase.title}
        </Typography>
        <Typography variant="caption" sx={{ color: d.color.slate600, fontSize: 12 }}>
          {phase.description}
        </Typography>
      </Box>
      <Chip
        label={`${totalDrills} drills`}
        size="small"
        sx={{
          bgcolor: `${accent.badge}18`, color: accent.badge,
          fontWeight: 600, fontSize: 11, height: 26,
          border: `1px solid ${accent.badge}30`,
        }}
      />
    </Box>
  )
}

// ── Progress badge on lesson card ────────────────────────────────────────────

function ProgressBadge({ status, accuracy }: { status: "completed" | "in-progress" | "not-started"; accuracy: number | null }) {
  if (status === "completed") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CheckCircle size={16} color="#16A34A" strokeWidth={2.5} />
        {accuracy !== null && (
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16A34A" }}>
            {accuracy}%
          </Typography>
        )}
      </Box>
    )
  }
  if (status === "in-progress" && accuracy !== null) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ position: "relative", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress
            variant="determinate"
            value={accuracy}
            size={16}
            thickness={5}
            sx={{ color: "#F59E0B", position: "absolute" }}
          />
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#F59E0B" }}>
          {accuracy}%
        </Typography>
      </Box>
    )
  }
  return null
}

// ── Lesson card (viewport-triggered entrance + icon hover scale) ────────────

function LessonCard({ lesson, accent, interactive, staggerMs, progress, patientId }: {
  lesson: Lesson; accent: typeof PHASE_ACCENTS[0]; interactive: boolean; staggerMs: number; progress?: LiteracyProgress | null; patientId?: string
}) {
  const { ref, visible } = useInView()
  const navigate = useNavigate()
  const icon = getLessonIcon(lesson.title)
  const status = progress ? getLessonStatus(lesson.id, progress) : "not-started"
  const accuracy = progress ? getLessonAccuracy(lesson.id, progress) : null
  const isCompleted = status === "completed"

  const handleClick = () => {
    if (patientId) {
      navigate(`/patients/${patientId}/literacy/lesson/${lesson.id}`)
    } else {
      navigate(`/literacy/lesson/${lesson.id}`)
    }
  }

  return (
    <Box
      ref={ref}
      onClick={handleClick}
      sx={{
        p: 2.5,
        borderRadius: d.radius.lg,
        border: `1px solid ${isCompleted ? "#86EFAC" : d.color.slate200}`,
        background: isCompleted
          ? "linear-gradient(180deg, #F0FDF4 0%, #ECFDF5 100%)"
          : "linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)",
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        ...(visible ? { animation: `litFadeInUp 450ms cubic-bezier(0.16,1,0.3,1) ${staggerMs}ms both` } : {}),
        display: "flex", alignItems: "flex-start", gap: 1.5,
        "&:hover .lit-icon": { transform: "scale(1.15)" },
        "&:hover": {
          transform: "translateY(-2px) scale(1.01)",
          borderColor: isCompleted ? "#34D399" : accent.border,
          boxShadow: `0 12px 28px ${accent.badge}14, 0 4px 10px rgba(0,0,0,0.06)`,
          background: isCompleted
            ? "linear-gradient(180deg, #F0FDF4 0%, #D1FAE5 100%)"
            : `linear-gradient(180deg, #FFFFFF 0%, ${accent.border}08 100%)`,
        },
      }}
    >
      <Box
        className="lit-icon"
        sx={{
          width: 40, height: 40, borderRadius: "50%",
          bgcolor: isCompleted ? "#D1FAE520" : `${accent.badge}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
          transition: "transform 200ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.25 }}>
          <Typography sx={{ fontWeight: 600, color: d.color.slate800, fontSize: 14, lineHeight: 1.3 }}>
            {lesson.title}
          </Typography>
          {interactive && <ProgressBadge status={status} accuracy={accuracy} />}
        </Box>
        <DrillDots count={lesson.drills.length} accent={accent.badge} animate={visible} />
      </Box>
    </Box>
  )
}

// ── Explore banner (gradient background) ────────────────────────────────────

function ExploreBanner() {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null

  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 2, mb: 3, borderRadius: d.radius.lg,
        background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 50%, #CFFAFE 100%)",
        border: `1px solid ${d.color.emeraldBorder}`,
        boxShadow: "0 2px 8px rgba(16,185,129,0.08)",
      }}
    >
      <InfoOutlinedIcon sx={{ color: d.color.emeraldDark, fontSize: 20, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ color: d.color.emeraldDarkest, fontSize: 13, flex: 1, fontWeight: 500 }}>
        You're browsing in explore mode. Select a patient from the dashboard to track progress and unlock drills.
      </Typography>
      <Box
        onClick={() => setDismissed(true)}
        sx={{ cursor: "pointer", color: d.color.emeraldDark, opacity: 0.5, "&:hover": { opacity: 1 }, flexShrink: 0, transition: "opacity 150ms ease" }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </Box>
    </Box>
  )
}

// ── Shared curriculum grid ────────────────────────────────────────────────────

function CurriculumGrid({ subtitle, interactive, showExploreBanner, progress, patientId }: {
  subtitle: string; interactive: boolean; showExploreBanner?: boolean; progress?: LiteracyProgress | null; patientId?: string
}) {
  ensureKeyframes()

  // Global stagger counter — each card gets a slightly later delay within its phase
  let globalCard = 0

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto", scrollBehavior: "smooth" }}>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <MenuBookIcon sx={{ color: d.color.emerald, fontSize: 34 }} />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800, fontSize: { xs: 26, sm: 32 },
            background: d.gradient.emerald,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            lineHeight: 1.2,
          }}
        >
          Lightspeed Literacy
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: d.color.slate500, mb: 4, fontSize: 14, ml: 0.5 }}>
        {subtitle}
      </Typography>

      {showExploreBanner && <ExploreBanner />}

      {curriculum.phases.map((phase, phaseIdx) => {
        const accent = PHASE_ACCENTS[phaseIdx % PHASE_ACCENTS.length]
        const totalDrills = phase.lessons.reduce((sum, l) => sum + l.drills.length, 0)
        // Reset stagger per phase so cards within each visible phase animate sequentially
        let phaseCard = 0
        return (
          <Box key={phase.id} sx={{ mb: 5 }}>
            <PhaseHeader phase={phase} accent={accent} totalDrills={totalDrills} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              {phase.lessons.map((lesson) => {
                const stagger = phaseCard * 50
                phaseCard++
                globalCard++
                return (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    accent={accent}
                    interactive={interactive}
                    staggerMs={stagger}
                    progress={progress}
                    patientId={patientId}
                  />
                )
              })}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

// ── Patient curriculum (uses context hook) ───────────────────────────────────

function PatientCurriculumView({ patientId }: { patientId: string }) {
  const { progress, loading, loadProgress } = useLiteracy()
  const { name, initials } = usePatientInfo(patientId)

  React.useEffect(() => {
    loadProgress(patientId)
  }, [patientId, loadProgress])

  if (loading) return <LiteracyLoading />

  return (
    <>
      <PatientHeader name={name} initials={initials} patientId={patientId} />
      <CurriculumGrid
        subtitle="Tap any lesson to start a drill session."
        interactive
        progress={progress}
        patientId={patientId}
      />
    </>
  )
}

// ── Explore curriculum (no context needed) ───────────────────────────────────

function ExploreCurriculumView() {
  return (
    <CurriculumGrid
      subtitle="Explore the curriculum. Tap any lesson to preview."
      interactive={false}
      showExploreBanner
    />
  )
}

// ── Progress dashboard ───────────────────────────────────────────────────────

function ProgressView({ patientId }: { patientId: string }) {
  const { progress, loading, loadProgress } = useLiteracy()
  const { name, initials } = usePatientInfo(patientId)

  React.useEffect(() => {
    loadProgress(patientId)
  }, [patientId, loadProgress])

  if (loading) return <LiteracyLoading />

  return (
    <>
      <PatientHeader name={name} initials={initials} patientId={patientId} />
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: d.color.slate800, mb: 3 }}>
          Literacy Progress
        </Typography>
        {!progress ? (
          <Typography variant="body2" sx={{ color: d.color.slate500 }}>
            No progress data yet. Start a lesson to begin tracking.
          </Typography>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ color: d.color.slate600, mb: 2 }}>
              Current phase: {progress.currentPhaseId} | Current lesson: {progress.currentLessonId}
            </Typography>
            <Typography variant="body2" sx={{ color: d.color.slate500 }}>
              Total time: {Math.round(progress.totalTimeSpent / 60000)} minutes
            </Typography>
          </Box>
        )}
      </Box>
    </>
  )
}

// ── Drill type metadata ──────────────────────────────────────────────────────

const DRILL_META: Record<string, { label: string; icon: React.ReactElement; description: string; color: string }> = {
  visual:          { label: "Visual Recognition",  icon: <Eye size={22} color="#2563EB" strokeWidth={2} />,       description: "See the letter or syllable, then say the sound",  color: "#2563EB" },
  auditory:        { label: "Auditory Drill",       icon: <Ear size={22} color="#7C3AED" strokeWidth={2} />,       description: "Hear the sound, then identify the letter",        color: "#7C3AED" },
  "air-writing":   { label: "Air Writing",          icon: <PencilLine size={22} color="#059669" strokeWidth={2} />, description: "Trace the letter in the air to build muscle memory", color: "#059669" },
  blending:        { label: "Sound Blending",       icon: <Layers size={22} color="#D97706" strokeWidth={2} />,    description: "Blend individual sounds together to read words",   color: "#D97706" },
  "speech-to-text":{ label: "Speech Practice",      icon: <Mic size={22} color="#DC2626" strokeWidth={2} />,       description: "Say the sound aloud and check your pronunciation", color: "#DC2626" },
  "text-to-speech":{ label: "Listening Comprehension", icon: <Speaker size={22} color="#0891B2" strokeWidth={2} />, description: "Listen to the system and respond",                 color: "#0891B2" },
}

function getDrillMeta(type: string) {
  return DRILL_META[type] ?? { label: type, icon: <Play size={22} color={ICON_COLOR} strokeWidth={2} />, description: "Practice drill", color: ICON_COLOR }
}

// ── Drill progress helper ──────────────────────────────────────────────────

function getDrillProgress(progress: LiteracyProgress | null, lessonId: string, drillType: string): DrillProgress | null {
  if (!progress?.phases) return null
  for (const phaseProgress of Object.values(progress.phases)) {
    const lp = (phaseProgress as any)?.lessons?.[lessonId]
    if (lp?.drills?.[drillType]) return lp.drills[drillType] as DrillProgress
  }
  return null
}

// ── Drill activity (flashcard-style practice) ──────────────────────────────

function DrillActivity({ drill, lessonId, patientId, onClose }: {
  drill: DrillConfig; lessonId: string; patientId?: string; onClose: () => void
}) {
  const { recordAttempt, progress } = useLiteracy()
  const meta = getDrillMeta(drill.type)
  const items = drill.type === "blending"
    ? (drill.blendingWords ?? []).map((w, i) => ({
        id: `blend-${i}`,
        type: "word" as const,
        content: w.word,
        phoneme: w.pattern,
        audioHint: undefined as string | undefined,
      }))
    : drill.items
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showAnswer, setShowAnswer] = useState(false)
  const [sessionAttempts, setSessionAttempts] = useState<{ correct: number; total: number }>({ correct: 0, total: 0 })
  const [sessionDone, setSessionDone] = useState(false)

  const item = items[currentIndex]
  const totalItems = items.length
  const drillProgress = getDrillProgress(progress, lessonId, drill.type)

  const handleResponse = useCallback(async (correct: boolean) => {
    if (!item) return
    const attempt: DrillAttempt = {
      itemId: item.id,
      correct,
      timestamp: Date.now(),
    }
    setSessionAttempts((prev) => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
    }))
    if (patientId) {
      await recordAttempt(patientId, lessonId, drill.type, attempt)
    }
    setShowAnswer(false)
    if (currentIndex < totalItems - 1) {
      setCurrentIndex((prev) => prev + 1)
    } else {
      setSessionDone(true)
    }
  }, [item, currentIndex, totalItems, patientId, lessonId, drill.type, recordAttempt])

  const handleRestart = () => {
    setCurrentIndex(0)
    setShowAnswer(false)
    setSessionAttempts({ correct: 0, total: 0 })
    setSessionDone(false)
  }

  if (!item && !sessionDone) return null

  const sessionAccuracy = sessionAttempts.total > 0
    ? Math.round((sessionAttempts.correct / sessionAttempts.total) * 100)
    : 0

  // Session complete screen
  if (sessionDone) {
    const mastered = sessionAccuracy >= drill.masteryThreshold
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: "auto", textAlign: "center" }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: "50%", mx: "auto", mb: 2,
          bgcolor: mastered ? "#D1FAE5" : "#FEF3C7",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {mastered
            ? <Award size={32} color="#059669" strokeWidth={2} />
            : <Target size={32} color="#D97706" strokeWidth={2} />}
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 22, color: d.color.slate800, mb: 0.5 }}>
          {mastered ? "Mastery Achieved" : "Session Complete"}
        </Typography>
        <Typography sx={{ color: d.color.slate500, fontSize: 14, mb: 3 }}>
          {sessionAttempts.correct} of {sessionAttempts.total} correct ({sessionAccuracy}%)
          {mastered ? "" : ` -- ${drill.masteryThreshold}% needed for mastery`}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Box
            onClick={handleRestart}
            sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 3, py: 1.5, borderRadius: d.radius.md,
              bgcolor: "#f1f5f9", cursor: "pointer", fontWeight: 600, fontSize: 14,
              color: d.color.slate700,
              transition: "all 0.2s ease",
              "&:hover": { bgcolor: "#e2e8f0" },
            }}
          >
            <RotateCcw size={16} /> Practice Again
          </Box>
          <Box
            onClick={onClose}
            sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 3, py: 1.5, borderRadius: d.radius.md,
              bgcolor: d.color.emerald, color: "#fff", cursor: "pointer",
              fontWeight: 600, fontSize: 14,
              transition: "all 0.2s ease",
              "&:hover": { bgcolor: d.color.emeraldDark },
            }}
          >
            <Check size={16} /> Done
          </Box>
        </Box>
      </Box>
    )
  }

  // Drill instruction text
  const instruction = drill.type === "visual"
    ? "Look at the syllable below, then say the sound aloud."
    : drill.type === "auditory"
    ? "Listen to the hint, then identify the letter or syllable."
    : drill.type === "air-writing"
    ? "Trace this letter or syllable in the air with your finger."
    : drill.type === "blending"
    ? "Blend the sounds together to read the word."
    : drill.type === "speech-to-text"
    ? "Say this sound aloud."
    : "Listen and respond."

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      {/* Header with close and progress */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {meta.icon}
          <Typography sx={{ fontWeight: 700, color: d.color.slate800, fontSize: 16 }}>
            {meta.label}
          </Typography>
        </Box>
        <Box
          onClick={onClose}
          sx={{
            width: 32, height: 32, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", transition: "all 0.2s ease",
            "&:hover": { bgcolor: "#f1f5f9" },
          }}
        >
          <X size={18} color={d.color.slate400} />
        </Box>
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: d.color.slate500 }}>
            Item {currentIndex + 1} of {totalItems}
          </Typography>
          {sessionAttempts.total > 0 && (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: sessionAccuracy >= drill.masteryThreshold ? "#059669" : d.color.slate400 }}>
              {sessionAccuracy}% accuracy
            </Typography>
          )}
        </Box>
        <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#e2e8f0", overflow: "hidden" }}>
          <Box sx={{
            height: "100%", borderRadius: 2,
            bgcolor: meta.color,
            width: `${((currentIndex + 1) / totalItems) * 100}%`,
            transition: "width 0.3s ease",
          }} />
        </Box>
      </Box>

      {/* Instruction */}
      <Typography sx={{ fontSize: 13, color: d.color.slate500, mb: 2, textAlign: "center" }}>
        {instruction}
      </Typography>

      {/* Flashcard */}
      <Box sx={{
        p: 4, borderRadius: d.radius.lg, textAlign: "center",
        border: `2px solid ${meta.color}30`,
        background: `linear-gradient(180deg, #fff 0%, ${meta.color}06 100%)`,
        mb: 3, minHeight: 180,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}>
        <Typography sx={{
          fontSize: 64, fontWeight: 800, color: d.color.slate800, lineHeight: 1.2,
          fontFamily: "'Georgia', serif",
          letterSpacing: 4,
        }}>
          {item.content}
        </Typography>
        {item.phoneme && (
          <Typography sx={{ fontSize: 16, color: d.color.slate500, mt: 1 }}>
            {item.phoneme}
          </Typography>
        )}
        {showAnswer && item.audioHint && (
          <Typography sx={{ fontSize: 14, color: meta.color, mt: 1.5, fontStyle: "italic" }}>
            {item.audioHint}
          </Typography>
        )}
      </Box>

      {/* Show hint / answer toggle */}
      {!showAnswer && item.audioHint && (
        <Box
          onClick={() => setShowAnswer(true)}
          sx={{
            textAlign: "center", mb: 2,
            color: d.color.slate400, fontSize: 13, fontWeight: 600,
            cursor: "pointer",
            "&:hover": { color: d.color.slate600 },
          }}
        >
          Show hint
        </Box>
      )}

      {/* Response buttons */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
        <Box
          onClick={() => handleResponse(false)}
          sx={{
            flex: 1, maxWidth: 200, py: 2, borderRadius: d.radius.md,
            textAlign: "center", cursor: "pointer",
            border: "2px solid #FCA5A5",
            bgcolor: "#FEF2F2", color: "#DC2626",
            fontWeight: 700, fontSize: 15,
            transition: "all 0.2s ease",
            "&:hover": { bgcolor: "#FEE2E2", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(220,38,38,0.15)" },
            "&:active": { transform: "translateY(0)" },
          }}
        >
          <X size={18} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />
          Incorrect
        </Box>
        <Box
          onClick={() => handleResponse(true)}
          sx={{
            flex: 1, maxWidth: 200, py: 2, borderRadius: d.radius.md,
            textAlign: "center", cursor: "pointer",
            border: "2px solid #86EFAC",
            bgcolor: "#F0FDF4", color: "#16A34A",
            fontWeight: 700, fontSize: 15,
            transition: "all 0.2s ease",
            "&:hover": { bgcolor: "#DCFCE7", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(22,163,74,0.15)" },
            "&:active": { transform: "translateY(0)" },
          }}
        >
          <Check size={18} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />
          Correct
        </Box>
      </Box>
    </Box>
  )
}

// ── Lesson view with drill cards ────────────────────────────────────────────

function LessonView({ patientId, lessonId }: { patientId?: string; lessonId?: string }) {
  const lesson = lessonId ? getLessonById(lessonId) : null
  const phase = lessonId ? getPhaseForLesson(lessonId) : null
  const { name, initials } = usePatientInfo(patientId)
  const navigate = useNavigate()
  const { progress, loading, loadProgress } = useLiteracy()
  const [activeDrill, setActiveDrill] = useState<DrillConfig | null>(null)

  React.useEffect(() => {
    if (patientId) loadProgress(patientId)
  }, [patientId, loadProgress])

  ensureKeyframes()

  const backPath = patientId
    ? `/patients/${patientId}/literacy`
    : "/literacy"

  const status = lesson && progress ? getLessonStatus(lesson.id, progress) : "not-started"
  const accuracy = lesson && progress ? getLessonAccuracy(lesson.id, progress) : null

  // When a drill is active, show the drill activity instead of the lesson overview
  if (activeDrill && lesson) {
    return (
      <>
        {patientId && <PatientHeader name={name} initials={initials} patientId={patientId} />}
        <DrillActivity
          drill={activeDrill}
          lessonId={lesson.id}
          patientId={patientId}
          onClose={() => setActiveDrill(null)}
        />
      </>
    )
  }

  return (
    <>
      {patientId && <PatientHeader name={name} initials={initials} patientId={patientId} />}
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        {/* Back link */}
        <Box
          onClick={() => navigate(backPath)}
          sx={{
            display: "inline-flex", alignItems: "center", gap: 0.5,
            color: d.color.emerald, cursor: "pointer", mb: 3,
            fontSize: 13, fontWeight: 600,
            transition: "color 0.2s ease, transform 0.2s ease",
            "&:hover": { color: d.color.emeraldDark, transform: "translateX(-2px)" },
          }}
        >
          <ArrowLeft size={16} />
          Back to Curriculum
        </Box>

        {!lesson ? (
          <Typography variant="body2" sx={{ color: d.color.slate500 }}>Lesson not found.</Typography>
        ) : (
          <>
            {/* Lesson header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "50%",
                bgcolor: phase ? `${PHASE_ACCENTS[(phase.number - 1) % PHASE_ACCENTS.length].badge}18` : "#f1f5f9",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                {getLessonIcon(lesson.title)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, color: d.color.slate800, fontSize: 22, lineHeight: 1.2 }}>
                  {lesson.title}
                </Typography>
                {phase && (
                  <Typography variant="caption" sx={{ color: d.color.slate400, fontSize: 12 }}>
                    Phase {phase.number}: {phase.title}
                  </Typography>
                )}
              </Box>
              {status !== "not-started" && <ProgressBadge status={status} accuracy={accuracy} />}
            </Box>

            <Typography variant="body2" sx={{ color: d.color.slate500, mb: 3, ml: 7.5 }}>
              {lesson.description}
            </Typography>

            {/* Drill cards grid */}
            <Typography sx={{ fontWeight: 700, color: d.color.slate700, fontSize: 15, mb: 1.5 }}>
              Drills ({lesson.drills.length})
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {lesson.drills.map((drill, i) => {
                const meta = getDrillMeta(drill.type)
                const itemCount = drill.type === "blending"
                  ? (drill.blendingWords?.length ?? 0)
                  : drill.items.length
                const dp = lesson ? getDrillProgress(progress, lesson.id, drill.type) : null
                const drillAccuracy = dp?.accuracy ?? null
                const isMastered = dp?.masteryAchieved ?? false
                return (
                  <Box
                    key={`${drill.type}-${i}`}
                    onClick={() => setActiveDrill(drill)}
                    sx={{
                      p: 2.5, borderRadius: d.radius.lg,
                      border: `1px solid ${isMastered ? "#86EFAC" : d.color.slate200}`,
                      background: isMastered
                        ? "linear-gradient(180deg, #F0FDF4 0%, #ECFDF5 100%)"
                        : "linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)",
                      cursor: "pointer",
                      opacity: 0,
                      animation: `litFadeInUp 450ms cubic-bezier(0.16,1,0.3,1) ${i * 80}ms both`,
                      transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        borderColor: meta.color,
                        boxShadow: `0 8px 24px ${meta.color}18, 0 2px 8px rgba(0,0,0,0.06)`,
                      },
                      display: "flex", flexDirection: "column", gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: d.radius.md,
                        bgcolor: `${meta.color}12`, border: `1px solid ${meta.color}25`,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        {meta.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: d.color.slate800, fontSize: 14, lineHeight: 1.3 }}>
                          {meta.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: d.color.slate400, fontSize: 11 }}>
                          {itemCount} {itemCount === 1 ? "item" : "items"} &middot; {drill.masteryThreshold}% mastery
                        </Typography>
                      </Box>
                      {isMastered && <CheckCircle size={18} color="#16A34A" strokeWidth={2.5} />}
                    </Box>
                    <Typography variant="body2" sx={{ color: d.color.slate500, fontSize: 13, lineHeight: 1.5 }}>
                      {meta.description}
                    </Typography>
                    {/* Progress badge or Start prompt */}
                    {dp && dp.attempts.length > 0 ? (
                      <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: 0.5,
                        px: 1.5, py: 0.75, borderRadius: d.radius.md,
                        bgcolor: isMastered ? "#D1FAE5" : "#f8fafc",
                        border: `1px solid ${isMastered ? "#86EFAC" : d.color.slate200}`,
                        alignSelf: "flex-start",
                      }}>
                        {isMastered
                          ? <Award size={12} color="#059669" />
                          : <Play size={12} color={meta.color} />}
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: isMastered ? "#059669" : d.color.slate600 }}>
                          {isMastered ? "Mastered" : `${drillAccuracy}% -- Continue`}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: 0.5,
                        px: 1.5, py: 0.75, borderRadius: d.radius.md,
                        bgcolor: `${meta.color}08`, border: `1px solid ${meta.color}20`,
                        alignSelf: "flex-start",
                      }}>
                        <Play size={12} color={meta.color} />
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: meta.color }}>
                          Start Drill
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )
              })}
            </Box>
          </>
        )}
      </Box>
    </>
  )
}

// ── Route exports (patient mode — wrapped in LiteracyProvider) ───────────────

export function LiteracyCurriculumRoute() {
  const { patientId } = useParams<{ patientId: string }>()
  if (!patientId) return null
  return (
    <LiteracyProvider>
      <PatientCurriculumView patientId={patientId} />
    </LiteracyProvider>
  )
}

export function LiteracyProgressRoute() {
  const { patientId } = useParams<{ patientId: string }>()
  if (!patientId) return null
  return (
    <LiteracyProvider>
      <ProgressView patientId={patientId} />
    </LiteracyProvider>
  )
}

export function LiteracyDashboardRoute() {
  const { patientId } = useParams<{ patientId: string }>()
  if (!patientId) return null
  return (
    <LiteracyProvider>
      <PatientCurriculumView patientId={patientId} />
    </LiteracyProvider>
  )
}

export function LiteracyLessonRoute() {
  const { patientId, lessonId } = useParams<{ patientId: string; lessonId: string }>()
  return (
    <LiteracyProvider>
      <LessonView patientId={patientId} lessonId={lessonId} />
    </LiteracyProvider>
  )
}

// ── Route exports (explore mode — no patient, no provider needed) ────────────

export function LiteracyExploreCurriculumRoute() {
  return <ExploreCurriculumView />
}

export function LiteracyExploreLessonRoute() {
  const { lessonId } = useParams<{ lessonId: string }>()
  return (
    <LiteracyProvider>
      <LessonView lessonId={lessonId} />
    </LiteracyProvider>
  )
}
